import { usableDuration } from './limits';

/**
 * One clock over the whole call, and the merged signal that carries it.
 *
 * Two things end a call by aborting: our own deadline and the caller's `signal`.
 * They travel together — the transport is handed ONE signal — so the whole
 * difficulty is telling afterwards which of the two fired, and that is the only
 * question this module answers.
 */

/** @see RequestOptions.timeout */
const DEFAULT_TIMEOUT_MS = 120_000;

/** Which of the two signals behind the merged one ended the call. */
export type EndedBy = 'caller' | 'deadline';

export interface Deadline {
  /** Settles with whoever ended the call. Stays pending while it runs. */
  readonly ended: Promise<EndedBy>;
  /** Whoever ended the call, or `undefined` while it is still running. */
  readonly endedBy: () => EndedBy | undefined;
  /**
   * How much of the clock is left, in ms. Negative once it has elapsed.
   *
   * `Infinity` when there is no clock, so that a caller comparing against it
   * gets the answer the absence means -- "nothing runs out" -- instead of a
   * zero that reads as "no time left".
   */
  readonly remaining: () => number;
  /** What the transport is handed: the caller's signal and ours, as one object. */
  readonly signal: AbortSignal;
  /** Stops the clock. */
  readonly stop: () => void;
}

/** A merged signal, and the cleanup whatever produced it needs. */
export interface Merged {
  /** Detaches whatever the merge attached to the sources. */
  readonly release: () => void;
  /** The sources as one signal, carrying the winner's reason unchanged. */
  readonly signal: AbortSignal;
}

/** Nothing to detach, because the platform attached nothing of ours. */
const NOTHING_TO_RELEASE = (): void => undefined;

/**
 * The sources as one signal, with our own merge where the runtime has none.
 *
 * WHY THE PLATFORM ONE STAYS THE DEFAULT RATHER THAN BEING REPLACED EVERYWHERE.
 * The DOM spec holds both the source-signal and the dependent-signal collections
 * in a WEAK SET, so `AbortSignal.any` never ties a long-lived caller signal to
 * every request made under it. A merge built on `addEventListener` holds its
 * listener STRONGLY: one `AbortController` in a component and a hundred calls
 * under it would leave a hundred listeners on a signal nobody drops. That is
 * what `release` is for, and it is the reason ours runs only where there is no
 * platform one to run instead.
 *
 * `AbortSignal.any` is Baseline 2024 and this repository's production
 * browserslist still resolves to chrome 103 and ios_saf 15.6, so a browser
 * bundle is the runtime in question; Node 22 has it. Asked here, per call,
 * rather than answered once at import: the answer belongs to the runtime the
 * call happens on, and nothing here is cheaper for having cached it.
 *
 * EXPORTED SO THAT THE JUDGEMENT ABOVE HAS EXACTLY ONE HOME (RC69). The
 * per-attempt clock in `request.ts` merges too, and a second `AbortSignal.any`
 * written there would behave perfectly on Node 22 and silently skip both the
 * hand-built path this keeps for the browsers we still target and the `release`
 * that goes with it. It becomes the second CALLER of this decision, never the
 * second author of it.
 */
export function mergeSignals(sources: AbortSignal[]): Merged {
  if (typeof AbortSignal.any === 'function') {
    return { release: NOTHING_TO_RELEASE, signal: AbortSignal.any(sources) };
  }

  /*
   * Whichever source wins, its reason is FORWARDED and never rebuilt. `decide`
   * below separates our own deadline from the caller's cancellation by object
   * identity alone (SF20), so a merge that constructs its own reason -- or even
   * copies this one -- makes that comparison false for every abort there is, and
   * our own elapsed deadline then reports as the caller having cancelled.
   * MEASURED: handing `source.reason` to `abort` leaves it the very same object.
   */
  const merged = new AbortController();

  // An already-aborted signal fires no event, so listeners alone would miss a
  // source that was over before the call began (SI3).
  const alreadyAborted = sources.find(source => source.aborted);
  if (alreadyAborted !== undefined) {
    merged.abort(alreadyAborted.reason);
    return { release: NOTHING_TO_RELEASE, signal: merged.signal };
  }

  const detach = sources.map(source => {
    const forward = (): void => merged.abort(source.reason);
    source.addEventListener('abort', forward, { once: true });
    return () => source.removeEventListener('abort', forward);
  });

  return {
    release: () => {
      for (const drop of detach) drop();
    },
    signal: merged.signal,
  };
}

/**
 * Starts the clock and merges the signals.
 *
 * An unusable number gets no clock at all, and that is not this module ignoring
 * it: `checkLimits` refuses the call for it, but only once the address has been
 * resolved enough to name in the failure. The clock has to start before that,
 * because a `before` handler spends the deadline too (TO28), so it would
 * otherwise be armed on a number the library has already decided it cannot act
 * on — and `setTimeout(fn, Infinity)` fires in 1 ms (TO8), which would report our
 * deadline for a call that is about to be refused as unsupported.
 */
export function startDeadline(
  timeout: number | undefined,
  caller: AbortSignal | undefined,
): Deadline {
  const ms = timeout ?? DEFAULT_TIMEOUT_MS;
  const ours = new AbortController();
  const startedAt = Date.now();

  /**
   * The one object that separates our deadline from the caller's cancellation.
   *
   * MEASURED (SF18): abort with a reason and `fetch` rejects with THAT VERY
   * OBJECT, and `AbortSignal.any` republishes the winner's reason unchanged. So
   * holding this is holding the answer.
   */
  const reason = new Error(`the ${ms} ms deadline for this request elapsed`);

  const armed = usableDuration(ms);
  const timer = armed ? setTimeout(() => ours.abort(reason), ms) : undefined;

  const merged = mergeSignals(
    caller === undefined ? [ours.signal] : [ours.signal, caller],
  );
  const signal = merged.signal;

  /**
   * IDENTITY, never the name.
   *
   * MEASURED (SF20): a caller's `AbortSignal.timeout` rejects with a
   * `DOMException` named `TimeoutError` — a caller's cancellation wearing the
   * name of ours. Deciding on the name reports it as our deadline and tells a
   * breaker to count a failure we did not cause. `reason` is an object nobody
   * outside this call holds, so comparing against it cannot be fooled.
   */
  const decide = (): EndedBy =>
    signal.reason === reason ? 'deadline' : 'caller';

  const ended = new Promise<EndedBy>(resolve => {
    // An already-aborted signal fires no event, so the listener alone would wait
    // forever on a call that is over before it began (SI3).
    if (signal.aborted) {
      resolve(decide());
      return;
    }
    signal.addEventListener('abort', () => resolve(decide()), { once: true });
  });

  return {
    ended,
    endedBy: () => (signal.aborted ? decide() : undefined),
    remaining: () =>
      armed ? startedAt + ms - Date.now() : Number.POSITIVE_INFINITY,
    signal,
    stop: () => {
      clearTimeout(timer);
      // For the same reason the timer is cleared here rather than only on abort:
      // a call that is over must stop holding onto anything the caller keeps.
      merged.release();
    },
  };
}
